import type { SqliteProvider } from '../providers/sqlite.provider';
import { BaseSqliteRepository } from './base-sqlite.repository';
import { format } from 'date-fns';
import type {
  AccountBalancesReportResult,
  ExpensesIncomesReportResult,
  GroupingDatesReportResult,
  IReportingRepository,
  ReportCategoryAndTagFilters,
  ReportFilterItem,
  StatementsReportResult,
} from '@app/domain/ports';
import type { GenerateReportConfig, ReportType } from '@app/domain/entities';
import { SqliteQueryError } from '../../errors';

type ReportConfigByType<TType extends ReportType> = Omit<Extract<GenerateReportConfig, { type: TType }>, 'type'>;

export class ReportingRepository extends BaseSqliteRepository implements IReportingRepository {
  constructor(provider: SqliteProvider) {
    super(provider);
  }

  async prepareAccountBalancesReportData(
    config: ReportConfigByType<ReportType.ACCOUNT_BALANCES>,
  ): Promise<AccountBalancesReportResult> {
    let dateFrom = config.dateFrom ?? new Date(0);
    let dateTo = config.dateTo ?? new Date();
    const kind: 'day' | 'month' | 'year' = config.groupBy;
    switch (config.groupBy) {
      case 'year':
        dateFrom = new Date(dateFrom.getFullYear(), 0, 1);
        dateTo = new Date(dateTo.getFullYear(), 0, 1);
        break;
      case 'month':
        dateFrom = new Date(dateFrom.getFullYear(), dateFrom.getMonth(), 1);
        dateTo = new Date(dateTo.getFullYear(), dateTo.getMonth(), 1);
        break;
      case 'day':
        dateFrom = new Date(dateFrom.getFullYear(), dateFrom.getMonth(), dateFrom.getDate());
        dateTo = new Date(dateTo.getFullYear(), dateTo.getMonth(), dateTo.getDate());
        break;
    }

    const hiddenAccountCondition = config.includeHiddenAccounts ? '' : 'and a.is_hidden = 0';
    const sql = `
      with recursive
      -- 0. Generate main dates
      intervals(interval_end) as (
        select ?
        union all
        select date(interval_end, '+1 ${kind}')
        from intervals
        where interval_end < ?
      ),
      -- 1. Calculate running totals for every single transaction in history
      all_history as (
        select
          d._id,
          d.balance_date,
          d.account_id,
          d.currency_id,
          round(sum(d.initial_balance + d.exchanges_amount + d.transfers_amount + d.incomes_amount - d.expenses_amount + d.creditors_amount - d.creditor_payments_amount - d.debtors_amount + d.debtor_payments_amount)
              over (partition by d.account_id, d.currency_id order by d.balance_date), 2) as running_amount
        from account_daily_balances d
        inner join accounts a
          on a._id = d.account_id
          and a.is_deleted = 0
          ${hiddenAccountCondition}
        inner join currencies c
          on c._id = d.currency_id
          and c.is_deleted = 0
      ),
      latest_per_interval AS (
        select
          unixepoch(i.interval_end) as interval_end,
          h.account_id,
          h.currency_id,
          max(h.balance_date) as last_update_date
        from intervals i
        cross join all_history h
        where h.balance_date < unixepoch(i.interval_end)
        group by i.interval_end, h.account_id, h.currency_id
      )
      select
        -- date(lpi.interval_end, 'unixepoch') as date,
        lpi.interval_end as date_part,
        cl.ticker as currencyCode,
        a.name as accountName,
        a.position as accountPosition,
        h.running_amount as amount
      from latest_per_interval lpi
      inner join all_history h
        on 1 = 1
          and h.account_id = lpi.account_id
          and h.currency_id = lpi.currency_id
          and h.balance_date = lpi.last_update_date
          and h.running_amount != 0
      inner join accounts a
        on a._id = lpi.account_id
      inner join currencies c
        on c._id = lpi.currency_id
        and c.is_deleted = 0
      inner join available_currencies cl
        on cl._id = c.available_currency_id
      order by lpi.interval_end asc, cl.ticker, a.position
    `;

    const start = format(dateFrom, 'yyyy-MM-dd');
    const end = format(dateTo, 'yyyy-MM-dd');

    //console.log(sql);
    //console.log([start, end]);

    const res = await this.query<{
      date_part: number;
      accountName: string;
      accountPosition: number;
      amount: number;
      currencyCode: string;
    }>(sql, [start, end]);

    return {
      options: {
        groupBy: config.groupBy,
      },
      filters: {
        dateFrom,
        dateTo,
        includeHiddenAccounts: config.includeHiddenAccounts === true,
      },
      data: {
        balances: this.pivotData(res, this.accountBalancesRowKey),
      },
    };
  }

  async prepareGroupingDatesReportData(
    config: ReportConfigByType<ReportType.GROUPING_BY_DATES>,
  ): Promise<GroupingDatesReportResult> {
    config = {
      ...config,
      dateTo: new Date(config.dateTo || new Date().setUTCHours(24, 0, 0, 0)),
    };

    const includeIncomeCategoryIds = this.normalizeIds(config.includeIncomeCategoryIds);
    const excludeIncomeCategoryIds = this.normalizeIds(config.excludeIncomeCategoryIds);
    const includeExpenseCategoryIds = this.normalizeIds(config.includeExpenseCategoryIds);
    const excludeExpenseCategoryIds = this.normalizeIds(config.excludeExpenseCategoryIds);
    const includeTagIds = this.normalizeIds(config.includeTagIds);
    const excludeTagIds = this.normalizeIds(config.excludeTagIds);

    const baseCurrencyCode = await this.getBaseCurrencyCode();
    const [incomes, expenses] = await Promise.all([
      this.generateGroupingDatesReportData('Incomes', {
        ...config,
        ...(config.includeIncomeCategoryIds !== undefined && {
          includeCategoryIds: includeIncomeCategoryIds,
        }),
        ...(config.excludeIncomeCategoryIds !== undefined && {
          excludeCategoryIds: excludeIncomeCategoryIds,
        }),
        ...(config.includeTagIds !== undefined && { includeTagIds }),
        ...(config.excludeTagIds !== undefined && { excludeTagIds }),
        currency: config.currency === 'base' ? baseCurrencyCode : config.currency,
      }),
      this.generateGroupingDatesReportData('Expenses', {
        ...config,
        ...(config.includeExpenseCategoryIds !== undefined && {
          includeCategoryIds: includeExpenseCategoryIds,
        }),
        ...(config.excludeExpenseCategoryIds !== undefined && {
          excludeCategoryIds: excludeExpenseCategoryIds,
        }),
        ...(config.includeTagIds !== undefined && { includeTagIds }),
        ...(config.excludeTagIds !== undefined && { excludeTagIds }),
        currency: config.currency === 'base' ? baseCurrencyCode : config.currency,
      }),
    ]);

    const reducer = (arr: { currencyCode: string; amount: number }[]) =>
      arr.reduce(
        (acc, item) => {
          const key = item.currencyCode ?? config.currency;
          acc[key] ??= 0;
          acc[key] += item.amount;

          return acc;
        },
        {} as Record<string, number>,
      );

    const incomesTotals = reducer(incomes);
    const expensesTotals = reducer(expenses);
    const totals: Record<string, number> = {};

    const currencies = Object.keys(incomesTotals).concat(Object.keys(expensesTotals));
    for (const currency of currencies) {
      totals[currency] = (incomesTotals[currency] ?? 0) - (expensesTotals[currency] ?? 0);
    }

    const { dateFrom, dateTo } = await this.getDateInterval(config.dateFrom, config.dateTo);
    const categoryAndTagFilters = await this.resolveCategoryAndTagFilters({
      includeIncomeCategoryIds,
      excludeIncomeCategoryIds,
      includeExpenseCategoryIds,
      excludeExpenseCategoryIds,
      includeTagIds,
      excludeTagIds,
    });

    return {
      options: {
        groupBy: config.groupBy,
        baseCurrencyCode,
      },
      filters: {
        dateFrom,
        dateTo,
        currency: config.currency,
        ...categoryAndTagFilters,
      },
      data: {
        incomes: this.pivotData(incomes, this.groupingDatesRowKey),
        expenses: this.pivotData(expenses, this.groupingDatesRowKey),
        incomesTotals,
        expensesTotals,
        totals,
      },
    };
  }

  async prepareExpensesIncomesReportData(
    config: ReportConfigByType<ReportType.EXPENSES_AND_INCOMES>,
  ): Promise<ExpensesIncomesReportResult> {
    config = {
      ...config,
      dateTo: new Date(config.dateTo || new Date().setUTCHours(24, 0, 0, 0)),
    };

    const includeIncomeCategoryIds = this.normalizeIds(config.includeIncomeCategoryIds);
    const excludeIncomeCategoryIds = this.normalizeIds(config.excludeIncomeCategoryIds);
    const includeExpenseCategoryIds = this.normalizeIds(config.includeExpenseCategoryIds);
    const excludeExpenseCategoryIds = this.normalizeIds(config.excludeExpenseCategoryIds);
    const includeTagIds = this.normalizeIds(config.includeTagIds);
    const excludeTagIds = this.normalizeIds(config.excludeTagIds);

    const baseCurrencyCode = await this.getBaseCurrencyCode();
    const [incomes, expenses] = await Promise.all([
      this.generateExpensesIncomesReportData('Incomes', {
        ...config,
        ...(config.includeIncomeCategoryIds !== undefined && {
          includeCategoryIds: includeIncomeCategoryIds,
        }),
        ...(config.excludeIncomeCategoryIds !== undefined && {
          excludeCategoryIds: excludeIncomeCategoryIds,
        }),
        ...(config.includeTagIds !== undefined && { includeTagIds }),
        ...(config.excludeTagIds !== undefined && { excludeTagIds }),
        currency: config.currency === 'base' ? baseCurrencyCode : config.currency,
      }),
      this.generateExpensesIncomesReportData('Expenses', {
        ...config,
        ...(config.includeExpenseCategoryIds !== undefined && {
          includeCategoryIds: includeExpenseCategoryIds,
        }),
        ...(config.excludeExpenseCategoryIds !== undefined && {
          excludeCategoryIds: excludeExpenseCategoryIds,
        }),
        ...(config.includeTagIds !== undefined && { includeTagIds }),
        ...(config.excludeTagIds !== undefined && { excludeTagIds }),
        currency: config.currency === 'base' ? baseCurrencyCode : config.currency,
      }),
    ]);

    const reducer = (arr: { currencyCode: string; amount: number }[]) =>
      arr.reduce(
        (acc, item) => {
          const key = item.currencyCode ?? config.currency;
          acc[key] ??= 0;
          acc[key] += item.amount;

          return acc;
        },
        {} as Record<string, number>,
      );

    const incomesTotals = reducer(incomes);
    const expensesTotals = reducer(expenses);
    const totals: Record<string, number> = {};

    const currencies = Object.keys(incomesTotals).concat(Object.keys(expensesTotals));
    for (const currency of currencies) {
      totals[currency] = (incomesTotals[currency] ?? 0) - (expensesTotals[currency] ?? 0);
    }

    const { dateFrom, dateTo } = await this.getDateInterval(config.dateFrom, config.dateTo);
    const categoryAndTagFilters = await this.resolveCategoryAndTagFilters({
      includeIncomeCategoryIds,
      excludeIncomeCategoryIds,
      includeExpenseCategoryIds,
      excludeExpenseCategoryIds,
      includeTagIds,
      excludeTagIds,
    });

    return {
      options: {
        baseCurrencyCode,
        includeAccounts: config.includeAccounts,
        includeCategories: config.includeCategories,
        includeSubcategories: config.includeSubcategories,
      },
      filters: {
        dateFrom,
        dateTo,
        currency: config.currency,
        invertFilters: config.invertFilters ?? false,
        ...categoryAndTagFilters,
      },
      data: {
        incomes: this.pivotData(incomes, this.expensesIncomesReportRowKey),
        expenses: this.pivotData(expenses, this.expensesIncomesReportRowKey),
        incomesTotals,
        expensesTotals,
        totals,
      },
    };
  }

  async prepareStatementsReportData(
    config: ReportConfigByType<ReportType.STATEMENTS>,
  ): Promise<StatementsReportResult> {
    const dateParams: unknown[] = [];
    if (config.dateFrom) dateParams.push(new Date(config.dateFrom).setUTCHours(0, 0, 0, 0) / 1000);
    if (config.dateTo) dateParams.push(new Date(config.dateTo).setUTCHours(24, 0, 0, 0) / 1000);

    const dateFilterIncome =
      config.dateFrom && config.dateTo
        ? 'AND i.income_date >= ? AND i.income_date < ?'
        : config.dateFrom
          ? 'AND i.income_date >= ?'
          : config.dateTo
            ? 'AND i.income_date < ?'
            : '';

    const dateFilterExpense =
      config.dateFrom && config.dateTo
        ? 'AND e.expense_date >= ? AND e.expense_date < ?'
        : config.dateFrom
          ? 'AND e.expense_date >= ?'
          : config.dateTo
            ? 'AND e.expense_date < ?'
            : '';

    const shouldFilterCurrency = config.currency !== undefined && config.currency !== 'all';
    const currencyFilter = shouldFilterCurrency ? 'AND cl.ticker = ?' : '';
    const baseParams = shouldFilterCurrency ? [...dateParams, config.currency] : [...dateParams];

    const includeIncomeCategoryIds = this.normalizeIds(config.includeIncomeCategoryIds);
    const excludeIncomeCategoryIds = this.normalizeIds(config.excludeIncomeCategoryIds);
    const includeExpenseCategoryIds = this.normalizeIds(config.includeExpenseCategoryIds);
    const excludeExpenseCategoryIds = this.normalizeIds(config.excludeExpenseCategoryIds);
    const includeTagIds = this.normalizeIds(config.includeTagIds);
    const excludeTagIds = this.normalizeIds(config.excludeTagIds);

    const includeIncomeCategoriesFilter =
      includeIncomeCategoryIds.length > 0 ? includeIncomeCategoryIds.map(() => '?').join(', ') : '';
    const excludeIncomeCategoriesFilter =
      excludeIncomeCategoryIds.length > 0 ? excludeIncomeCategoryIds.map(() => '?').join(', ') : '';
    const includeExpenseCategoriesFilter =
      includeExpenseCategoryIds.length > 0 ? includeExpenseCategoryIds.map(() => '?').join(', ') : '';
    const excludeExpenseCategoriesFilter =
      excludeExpenseCategoryIds.length > 0 ? excludeExpenseCategoryIds.map(() => '?').join(', ') : '';

    const includeTagsFilter = includeTagIds
      .map(() => 'AND EXISTS (SELECT 1 FROM expense_tags et WHERE et.expense_id = e._id AND et.tag_id = ?)')
      .join('\n        ');

    const excludeTagsFilter = excludeTagIds
      .map(() => 'AND NOT EXISTS (SELECT 1 FROM expense_tags et WHERE et.expense_id = e._id AND et.tag_id = ?)')
      .join('\n        ');

    const incomeParams = [
      ...baseParams,
      ...includeIncomeCategoryIds,
      ...includeIncomeCategoryIds,
      ...excludeIncomeCategoryIds,
      ...excludeIncomeCategoryIds,
    ];
    const expenseParams = [
      ...baseParams,
      ...includeExpenseCategoryIds,
      ...includeExpenseCategoryIds,
      ...excludeExpenseCategoryIds,
      ...excludeExpenseCategoryIds,
      ...includeTagIds,
      ...excludeTagIds,
    ];

    const incomesSql = `
      SELECT
        i.income_date AS date_ts,
        acc.name AS account,
        i.amount AS amount,
        cl.ticker AS currencyCode,
        i.note AS description,
        iif(pcat._id IS NULL, cat.name, pcat.name) AS category,
        iif(pcat._id IS NULL, NULL, cat.name) AS subcategory
      FROM incomes i
      INNER JOIN accounts acc ON acc._id = i.account_id
      INNER JOIN currencies cur ON cur._id = i.currency_id
      INNER JOIN available_currencies cl ON cl._id = cur.available_currency_id
      INNER JOIN categories cat ON cat._id = i.category_id
      LEFT JOIN categories pcat ON pcat._id = cat.parent_category_id
      WHERE i.is_deleted = 0
        ${dateFilterIncome}
        ${currencyFilter}
        ${includeIncomeCategoryIds.length > 0 ? `AND (cat._id IN (${includeIncomeCategoriesFilter}) OR pcat._id IN (${includeIncomeCategoriesFilter}))` : ''}
        ${excludeIncomeCategoryIds.length > 0 ? `AND NOT (cat._id IN (${excludeIncomeCategoriesFilter}) OR pcat._id IN (${excludeIncomeCategoriesFilter}))` : ''}
      ORDER BY i.income_date DESC, i._id DESC
    `;

    const expensesSql = `
      SELECT
        e.expense_date AS date_ts,
        acc.name AS account,
        e.amount AS amount,
        cl.ticker AS currencyCode,
        e.note AS description,
        e.merchant_category_code AS mcc,
        iif(pcat._id IS NULL, cat.name, pcat.name) AS category,
        iif(pcat._id IS NULL, NULL, cat.name) AS subcategory
      FROM expenses e
      INNER JOIN accounts acc ON acc._id = e.account_id
      INNER JOIN currencies cur ON cur._id = e.currency_id
      INNER JOIN available_currencies cl ON cl._id = cur.available_currency_id
      INNER JOIN categories cat ON cat._id = e.category_id
      LEFT JOIN categories pcat ON pcat._id = cat.parent_category_id
      WHERE e.is_deleted = 0
        ${dateFilterExpense}
        ${currencyFilter}
        ${includeExpenseCategoryIds.length > 0 ? `AND (cat._id IN (${includeExpenseCategoriesFilter}) OR pcat._id IN (${includeExpenseCategoriesFilter}))` : ''}
        ${excludeExpenseCategoryIds.length > 0 ? `AND NOT (cat._id IN (${excludeExpenseCategoriesFilter}) OR pcat._id IN (${excludeExpenseCategoriesFilter}))` : ''}
        ${includeTagsFilter}
        ${excludeTagsFilter}
      ORDER BY e.expense_date DESC, e._id DESC
    `;

    const [rawIncomes, rawExpenses] = await Promise.all([
      this.query<{
        date_ts: number;
        account: string;
        amount: number;
        currencyCode: string;
        description?: string;
        category: string;
        subcategory?: string;
      }>(incomesSql, incomeParams),
      this.query<{
        date_ts: number;
        account: string;
        amount: number;
        currencyCode: string;
        description?: string;
        mcc?: number;
        category: string;
        subcategory?: string;
      }>(expensesSql, expenseParams),
    ]);

    const incomes = rawIncomes.map((r) => ({
      date: new Date(r.date_ts * 1000),
      account: r.account,
      amount: r.amount,
      currencyCode: r.currencyCode,
      ...(r.description ? { description: r.description } : {}),
      category: r.category,
      ...(r.subcategory ? { subcategory: r.subcategory } : {}),
    }));

    const expenses = rawExpenses.map((r) => ({
      date: new Date(r.date_ts * 1000),
      account: r.account,
      amount: r.amount,
      currencyCode: r.currencyCode,
      ...(r.description ? { description: r.description } : {}),
      ...(r.mcc != null ? { mcc: r.mcc } : {}),
      category: r.category,
      ...(r.subcategory ? { subcategory: r.subcategory } : {}),
    }));

    const { dateFrom, dateTo } = await this.getDateInterval(config.dateFrom, config.dateTo);
    const categoryAndTagFilters = await this.resolveCategoryAndTagFilters({
      includeIncomeCategoryIds,
      excludeIncomeCategoryIds,
      includeExpenseCategoryIds,
      excludeExpenseCategoryIds,
      includeTagIds,
      excludeTagIds,
    });

    return {
      options: {},
      filters: {
        dateFrom: incomes.length === 0 && expenses.length === 0 ? config.dateFrom : dateFrom,
        dateTo: incomes.length === 0 && expenses.length === 0 ? config.dateTo : dateTo,
        currency: config.currency,
        ...categoryAndTagFilters,
      },
      data: {
        incomes,
        expenses,
      },
    };
  }

  private normalizeIds(ids?: number[]): number[] {
    return Array.from(new Set(ids ?? [])).filter((id) => Number.isInteger(id) && id > 0);
  }

  private async resolveCategoryAndTagFilters(input: {
    includeIncomeCategoryIds: number[];
    excludeIncomeCategoryIds: number[];
    includeExpenseCategoryIds: number[];
    excludeExpenseCategoryIds: number[];
    includeTagIds: number[];
    excludeTagIds: number[];
  }): Promise<ReportCategoryAndTagFilters> {
    const [
      includeIncomeCategories,
      excludeIncomeCategories,
      includeExpenseCategories,
      excludeExpenseCategories,
      includeTags,
      excludeTags,
    ] = await Promise.all([
      this.resolveCategoryFilterItems(input.includeIncomeCategoryIds),
      this.resolveCategoryFilterItems(input.excludeIncomeCategoryIds),
      this.resolveCategoryFilterItems(input.includeExpenseCategoryIds),
      this.resolveCategoryFilterItems(input.excludeExpenseCategoryIds),
      this.resolveTagFilterItems(input.includeTagIds),
      this.resolveTagFilterItems(input.excludeTagIds),
    ]);

    return {
      includeIncomeCategories,
      excludeIncomeCategories,
      includeExpenseCategories,
      excludeExpenseCategories,
      includeTags,
      excludeTags,
    };
  }

  private async resolveCategoryFilterItems(ids: number[]): Promise<ReportFilterItem[]> {
    if (ids.length === 0) return [];

    const placeholders = ids.map(() => '?').join(', ');
    const rows = await this.query<{ id: number; name: string; parentName: string | null }>(
      `
      SELECT
        c._id AS id,
        c.name AS name,
        p.name AS parentName
      FROM categories c
      LEFT JOIN categories p
        ON p._id = c.parent_category_id
        AND p.is_deleted = 0
      WHERE c._id IN (${placeholders})
        AND c.is_deleted = 0
      `,
      ids,
    );

    const byId = new Map(rows.map((row) => [row.id, row]));
    return ids
      .map((id) => byId.get(id))
      .filter((row): row is { id: number; name: string; parentName: string | null } => Boolean(row))
      .map((row) => ({
        id: row.id,
        title: row.parentName ? `${row.parentName} / ${row.name}` : row.name,
      }));
  }

  private async resolveTagFilterItems(ids: number[]): Promise<ReportFilterItem[]> {
    if (ids.length === 0) return [];

    const placeholders = ids.map(() => '?').join(', ');
    const rows = await this.query<{ id: number; title: string }>(
      `
      SELECT
        t._id AS id,
        t.name AS title
      FROM tags t
      WHERE t._id IN (${placeholders})
      `,
      ids,
    );

    const byId = new Map(rows.map((row) => [row.id, row]));
    return ids
      .map((id) => byId.get(id))
      .filter((row): row is { id: number; title: string } => Boolean(row))
      .map((row) => ({
        id: row.id,
        title: row.title,
      }));
  }

  private async getDateInterval(dateFrom?: Date, dateTo?: Date): Promise<{ dateFrom: Date; dateTo: Date }> {
    const start = dateFrom ? new Date(dateFrom).setUTCHours(0, 0, 0, 0) / 1000 : 0;
    const end = (dateTo ? new Date(dateTo) : new Date()).setUTCHours(24, 0, 0, 0) / 1000;

    const sql = `
      select
        min(date_field) as dateFrom,
        max(date_field) as dateTo
      from (
        select income_date as date_field from incomes i
        where i.income_date between ? and  ? and i.is_deleted = 0
        union
        select expense_date as date_field from expenses e
        where e.expense_date >= ? and e.expense_date < ? and e.is_deleted = 0
      )
    `;

    const [res] = await this.query<{ dateFrom: number; dateTo: number }>(sql, [start, end, start, end]);
    return { dateFrom: new Date(res.dateFrom * 1000), dateTo: new Date(res.dateTo * 1000) };
  }

  private async getBaseCurrencyCode(): Promise<string> {
    const result = await this.query<{ code: string }>(
      `
      SELECT
        cl.ticker AS code
      FROM currencies AS c
      INNER JOIN available_currencies AS cl
        ON c.available_currency_id == cl._id
      WHERE c.is_default = 1 and c.is_deleted = 0
      LIMIT 1
    `,
    );

    return result.length > 0 ? result[0].code : 'USD';
  }

  private async getBaseCurrencyId(): Promise<number> {
    const result = await this.query<{ id: number }>(
      `
      SELECT
        c._id AS id
      FROM currencies AS c
      WHERE c.is_default = 1 and c.is_deleted = 0
      LIMIT 1
    `,
    );

    if (result.length === 0) {
      throw new SqliteQueryError('SELECT ...', [], 'Base currency not found');
    }

    return result[0].id;
  }

  private async generateGroupingDatesReportData(
    table: 'Incomes' | 'Expenses',
    config: {
      groupBy: 'day' | 'month' | 'year';
      dateFrom?: Date | undefined;
      dateTo?: Date | undefined;
      currency: 'all' | string;
      includeCategoryIds?: number[] | undefined;
      excludeCategoryIds?: number[] | undefined;
      includeTagIds?: number[] | undefined;
      excludeTagIds?: number[] | undefined;
    },
  ): Promise<
    {
      date_part: number;
      amount: number;
      currencyCode: string;
    }[]
  > {
    const oppositeTable = table === 'Incomes' ? 'Expenses' : 'Incomes';
    const ownTableName = table === 'Incomes' ? 'incomes' : 'expenses';
    const oppositeTableName = oppositeTable === 'Incomes' ? 'incomes' : 'expenses';
    const ownDateColumn = table === 'Incomes' ? 'income_date' : 'expense_date';
    const oppositeDateColumn = oppositeTable === 'Incomes' ? 'income_date' : 'expense_date';
    let aggregate = '';
    const columns: string[] = [];
    const filters: string[] = [];
    const params: unknown[] = [];
    const groupBy: string[] = [];
    const orderBy: string[] = [];

    if (config.groupBy === 'year') {
      columns.push("unixepoch(strftime('%Y-01-01', createdAt, 'unixepoch')) as date_part");
    }

    if (config.groupBy === 'month') {
      columns.push("unixepoch(strftime('%Y-%m-01', createdAt, 'unixepoch')) as date_part");
    }

    if (config.groupBy === 'day') {
      columns.push("unixepoch(strftime('%Y-%m-%d', createdAt, 'unixepoch')) as date_part");
    }

    groupBy.push('date_part');
    orderBy.push('date_part asc');

    // Get base currency ID if needed for conversion
    const baseCurrencyId = config.currency !== 'all' ? await this.getBaseCurrencyId() : null;

    if (config.currency == 'all') {
      columns.push('currencyCode');
      groupBy.push('currencyCode');
      aggregate = 'round(sum(amount), 2) as amount';
    } else {
      columns.push(`'${config.currency}' as currencyCode`);
      groupBy.push('currencyCode');

      // Smart conversion using CASE to avoid unnecessary conversions
      aggregate = `round(sum(
        CASE
          WHEN currencyCode = '${config.currency}' THEN amount
          WHEN currencyIsBase = 1 THEN amount / COALESCE(targetRate, 1)
          WHEN targetCurrencyId = baseCurrencyId THEN amount * rate
          ELSE amount * rate / COALESCE(targetRate, 1)
        END
      ), 2) as amount`;
    }

    if (config.dateFrom) {
      filters.push(`createdAt >= ?`);
      params.push(new Date(config.dateFrom).setUTCHours(0, 0, 0, 0) / 1000);
    }

    if (config.dateTo) {
      filters.push(`createdAt < ?`);
      params.push(new Date(config.dateTo).setUTCHours(0, 0, 0, 0) / 1000);
    }

    const includeCategoryIds = Array.from(new Set(config.includeCategoryIds ?? [])).filter(
      (id) => Number.isInteger(id) && id > 0,
    );
    const excludeCategoryIds = Array.from(new Set(config.excludeCategoryIds ?? [])).filter(
      (id) => Number.isInteger(id) && id > 0,
    );

    if (includeCategoryIds.length > 0) {
      const placeholders = includeCategoryIds.map(() => '?').join(', ');
      // A total-only offset has no category. Exclude it from include-only reports
      // so, for example, an unattributed reimbursement cannot change a Food-only total.
      filters.push(`(categoryId IN (${placeholders}) OR parentCategoryId IN (${placeholders}))`);
      params.push(...includeCategoryIds, ...includeCategoryIds);
    }

    if (excludeCategoryIds.length > 0) {
      const placeholders = excludeCategoryIds.map(() => '?').join(', ');
      filters.push(
        `(categoryId IS NULL OR NOT (categoryId IN (${placeholders}) OR parentCategoryId IN (${placeholders})))`,
      );
      params.push(...excludeCategoryIds, ...excludeCategoryIds);
    }

    // Tags filter expense source rows only. Income source rows have NULL expenseId
    // and remain eligible, including income reimbursements reducing expenses.
    const includeTagIds = Array.from(new Set(config.includeTagIds ?? [])).filter(
      (id) => Number.isInteger(id) && id > 0,
    );
    const excludeTagIds = Array.from(new Set(config.excludeTagIds ?? [])).filter(
      (id) => Number.isInteger(id) && id > 0,
    );

    if (includeTagIds.length > 0) {
      const placeholders = includeTagIds.map(() => '?').join(', ');
      filters.push(
        `(expenseId IS NULL OR expenseId IN (SELECT expense_id FROM expense_tags WHERE tag_id IN (${placeholders})))`,
      );
      params.push(...includeTagIds);
    }

    if (excludeTagIds.length > 0) {
      const placeholders = excludeTagIds.map(() => '?').join(', ');
      filters.push(
        `(expenseId IS NULL OR expenseId NOT IN (SELECT expense_id FROM expense_tags WHERE tag_id IN (${placeholders})))`,
      );
      params.push(...excludeTagIds);
    }

    const columnsClause = columns.length > 0 ? `${columns.join(', ')}, ${aggregate}` : aggregate;
    const whereClause = filters.length > 0 ? `WHERE ${filters.join(' AND ')}` : '';
    const orderByClause = orderBy.length > 0 ? `ORDER BY ${orderBy.join(', ')}` : '';
    const groupByClause = groupBy.length > 0 ? `GROUP BY ${groupBy.join(', ')}` : '';

    // Build single SQL with optional target_info CTE
    const targetCurrencyCTE =
      config.currency !== 'all'
        ? `target_info as (
          select c._id as target_id
          from currencies c
          inner join available_currencies cl on c.available_currency_id = cl._id
          where cl.ticker = ? and c.is_deleted = 0
          limit 1
        ),`
        : '';

    const conversionColumns = (alias: string, dateColumn: string) =>
      config.currency !== 'all'
        ? `,
            cur.is_default as currencyIsBase,
            coalesce(${alias}.exchange_rate, 1) as rate,
            coalesce((
              select r.rate from exchange_rates r
              where r.currency_id = (select target_id from target_info)
                and r.is_deleted = 0
                and r.rate_date <= ${alias}.${dateColumn}
              order by r.rate_date desc
              limit 1
            ), 1) as targetRate,
            (select target_id from target_info) as targetCurrencyId,
            ${baseCurrencyId} as baseCurrencyId`
        : `,
            coalesce(${alias}.exchange_rate, 1) as rate`;

    const sql = `
      with
      ${targetCurrencyCTE}
      raw_data as (
        select
          own.${ownDateColumn} as createdAt,
          ${table === 'Expenses' ? 'own._id' : 'NULL'} as expenseId,
          cat._id as categoryId,
          pcat._id as parentCategoryId,
          own.amount as amount,
          cl.ticker as currencyCode${conversionColumns('own', ownDateColumn)}
        from ${ownTableName} as own
        inner join currencies cur
          on cur._id == own.currency_id
        inner join available_currencies cl
          on cl._id == cur.available_currency_id
        inner join categories cat
          on cat._id == own.category_id
        left join categories pcat
          on pcat._id == cat.parent_category_id
        where own.is_deleted == 0
          and coalesce(cat.reduces_opposite_type, 0) = 0

        union all

        select
          opposite.${oppositeDateColumn} as createdAt,
          ${oppositeTable === 'Expenses' ? 'opposite._id' : 'NULL'} as expenseId,
          target._id as categoryId,
          target_parent._id as parentCategoryId,
          -opposite.amount as amount,
          cl.ticker as currencyCode${conversionColumns('opposite', oppositeDateColumn)}
        from ${oppositeTableName} as opposite
        inner join currencies cur
          on cur._id == opposite.currency_id
        inner join available_currencies cl
          on cl._id == cur.available_currency_id
        inner join categories source_cat
          on source_cat._id == opposite.category_id
        left join categories target
          on target._id == source_cat.offset_category_id
          and target.is_deleted = 0
        left join categories target_parent
          on target_parent._id == target.parent_category_id
        where opposite.is_deleted == 0
          and coalesce(source_cat.reduces_opposite_type, 0) = 1
      )
      select
        ${columnsClause}
      from raw_data
      ${whereClause}
      ${groupByClause}
      ${orderByClause}
    `;

    const finalParams = config.currency !== 'all' ? [config.currency, ...params] : params;

    return this.query<{ date_part: number; amount: number; currencyCode: string }>(sql, finalParams);
  }

  private async generateExpensesIncomesReportData(
    table: 'Incomes' | 'Expenses',
    config: {
      dateFrom?: Date | undefined;
      dateTo?: Date | undefined;
      currency: 'all' | string;
      includeCategoryIds?: number[] | undefined;
      excludeCategoryIds?: number[] | undefined;
      includeTagIds?: number[] | undefined;
      excludeTagIds?: number[] | undefined;
      includeAccounts: boolean;
      includeCategories: boolean;
      includeSubcategories: boolean;
      invertFilters?: boolean;
    },
  ): Promise<
    Array<{
      account?: string | undefined;
      category?: string | undefined;
      subcategory?: string | undefined;
      amount: number;
      currencyCode: string;
    }>
  > {
    const oppositeTable = table === 'Incomes' ? 'Expenses' : 'Incomes';
    const ownTableName = table === 'Incomes' ? 'incomes' : 'expenses';
    const oppositeTableName = oppositeTable === 'Incomes' ? 'incomes' : 'expenses';
    const ownDateColumn = table === 'Incomes' ? 'income_date' : 'expense_date';
    const oppositeDateColumn = oppositeTable === 'Incomes' ? 'income_date' : 'expense_date';
    let aggregate = '';
    const columns: string[] = [];
    const filters: string[] = [];
    const params: unknown[] = [];
    const groupBy: string[] = [];
    const orderBy: string[] = [];

    if (config.includeAccounts) {
      columns.push('account');
      groupBy.push('account');
      orderBy.push('accountPosition asc');
    }

    if (config.includeCategories) {
      columns.push('category');
      groupBy.push('category');
      orderBy.push('categoryPosition asc');
    }

    if (config.includeSubcategories) {
      columns.push('subcategory');
      groupBy.push('subcategory');
      orderBy.push('subcategoryPosition asc');
    }

    // Get base currency ID if needed for conversion
    const baseCurrencyId = config.currency !== 'all' ? await this.getBaseCurrencyId() : null;

    if (config.currency == 'all') {
      columns.push('currencyCode');
      groupBy.push('currencyCode');
      aggregate = 'round(sum(amount), 2) as amount';
    } else {
      columns.push(`'${config.currency}' as currencyCode`);
      groupBy.push('currencyCode');

      // Smart conversion using CASE to avoid unnecessary conversions
      aggregate = `round(sum(
        CASE
          WHEN currencyCode = '${config.currency}' THEN amount
          WHEN currencyIsBase = 1 THEN amount / COALESCE(targetRate, 1)
          WHEN targetCurrencyId = baseCurrencyId THEN amount * rate
          ELSE amount * rate / COALESCE(targetRate, 1)
        END
      ), 2) as amount`;
    }

    if (config.dateFrom) {
      filters.push(`createdAt >= ?`);
      params.push(new Date(config.dateFrom).setUTCHours(0, 0, 0, 0) / 1000);
    }

    if (config.dateTo) {
      filters.push(`createdAt < ?`);
      params.push(new Date(config.dateTo).setUTCHours(0, 0, 0, 0) / 1000);
    }

    const includeCategoryIds = Array.from(new Set(config.includeCategoryIds ?? [])).filter(
      (id) => Number.isInteger(id) && id > 0,
    );
    const excludeCategoryIds = Array.from(new Set(config.excludeCategoryIds ?? [])).filter(
      (id) => Number.isInteger(id) && id > 0,
    );

    if (includeCategoryIds.length > 0) {
      const placeholders = includeCategoryIds.map(() => '?').join(', ');
      // Unattributed offsets intentionally do not affect an include-only category report.
      filters.push(`(categoryId IN (${placeholders}) OR parentCategoryId IN (${placeholders}))`);
      params.push(...includeCategoryIds, ...includeCategoryIds);
    }

    if (excludeCategoryIds.length > 0) {
      const placeholders = excludeCategoryIds.map(() => '?').join(', ');
      filters.push(
        `NOT (COALESCE((categoryId IN (${placeholders}) OR parentCategoryId IN (${placeholders})), 0))`,
      );
      params.push(...excludeCategoryIds, ...excludeCategoryIds);
    }

    const includeTagIds = Array.from(new Set(config.includeTagIds ?? [])).filter(
      (id) => Number.isInteger(id) && id > 0,
    );
    const excludeTagIds = Array.from(new Set(config.excludeTagIds ?? [])).filter(
      (id) => Number.isInteger(id) && id > 0,
    );

    if (includeTagIds.length > 0) {
      const placeholders = includeTagIds.map(() => '?').join(', ');
      filters.push(
        `(expenseId IS NULL OR expenseId IN (SELECT expense_id FROM expense_tags WHERE tag_id IN (${placeholders})))`,
      );
      params.push(...includeTagIds);
    }

    if (excludeTagIds.length > 0) {
      const placeholders = excludeTagIds.map(() => '?').join(', ');
      filters.push(
        `(expenseId IS NULL OR expenseId NOT IN (SELECT expense_id FROM expense_tags WHERE tag_id IN (${placeholders})))`,
      );
      params.push(...excludeTagIds);
    }

    // Negate the complete category/tag predicate, not its individual clauses.
    // Date restrictions remain outside this predicate.
    const categoryTagFilterStart = config.dateFrom ? 1 : 0;
    const categoryTagFilterOffset = categoryTagFilterStart + (config.dateTo ? 1 : 0);
    const scopedFilters = filters.slice(0, categoryTagFilterOffset);
    const categoryTagFilters = filters.slice(categoryTagFilterOffset);
    const effectiveFilters = config.invertFilters
      ? [...scopedFilters, categoryTagFilters.length ? `NOT (COALESCE((${categoryTagFilters.join(' AND ')}), 0))` : '0 = 1']
      : filters;

    const columnsClause = columns.length > 0 ? `${columns.join(', ')}, ${aggregate}` : aggregate;
    const whereClause = effectiveFilters.length > 0 ? `WHERE ${effectiveFilters.join(' AND ')}` : '';
    const orderByClause = orderBy.length > 0 ? `ORDER BY ${orderBy.join(', ')}` : '';
    const groupByClause = groupBy.length > 0 ? `GROUP BY ${groupBy.join(', ')}` : '';

    // Build single SQL with optional target_info CTE
    const targetCurrencyCTE =
      config.currency !== 'all'
        ? `target_info as (
          select c._id as target_id
          from currencies c
          inner join available_currencies cl on c.available_currency_id = cl._id
          where cl.ticker = ? and c.is_deleted = 0
          limit 1
        ),`
        : '';

    const conversionColumns = (alias: string, dateColumn: string) =>
      config.currency !== 'all'
        ? `,
            cur.is_default as currencyIsBase,
            coalesce(${alias}.exchange_rate, 1) as rate,
            coalesce((
              select r.rate from exchange_rates r
              where r.currency_id = (select target_id from target_info)
                and r.is_deleted = 0
                and r.rate_date <= ${alias}.${dateColumn}
              order by r.rate_date desc
              limit 1
            ), 1) as targetRate,
            (select target_id from target_info) as targetCurrencyId,
            ${baseCurrencyId} as baseCurrencyId`
        : `,
            coalesce(${alias}.exchange_rate, 1) as rate`;

    const sql = `
      with
      ${targetCurrencyCTE}
      raw_data as (
        select
          own.${ownDateColumn} as createdAt,
          ${table === 'Expenses' ? 'own._id' : 'NULL'} as expenseId,
          acc.name as account,
          acc.position as accountPosition,
          iif(pcat._id is null, cat.name, pcat.name) as category,
          iif(pcat._id is null, cat.position, pcat.position) as categoryPosition,
          iif(pcat._id is null, null, cat.name) as subcategory,
          iif(pcat._id is null, 0, cat.position) as subcategoryPosition,
          cat._id as categoryId,
          pcat._id as parentCategoryId,
          own.amount as amount,
          cl.ticker as currencyCode${conversionColumns('own', ownDateColumn)}
        from ${ownTableName} as own
        inner join accounts as acc
          on acc._id == own.account_id
        inner join currencies cur
          on cur._id == own.currency_id
        inner join available_currencies cl
          on cl._id == cur.available_currency_id
        inner join categories cat
          on cat._id == own.category_id
        left join categories pcat
          on pcat._id == cat.parent_category_id
        where own.is_deleted == 0
          and coalesce(cat.reduces_opposite_type, 0) = 0

        union all

        select
          opposite.${oppositeDateColumn} as createdAt,
          ${oppositeTable === 'Expenses' ? 'opposite._id' : 'NULL'} as expenseId,
          acc.name as account,
          acc.position as accountPosition,
          case
            when target._id is null then '__opposite_side_adjustments__'
            when target_parent._id is null then target.name
            else target_parent.name
          end as category,
          case
            when target._id is null then 2147483647
            when target_parent._id is null then target.position
            else target_parent.position
          end as categoryPosition,
          iif(target_parent._id is null, null, target.name) as subcategory,
          iif(target_parent._id is null, 0, target.position) as subcategoryPosition,
          target._id as categoryId,
          target_parent._id as parentCategoryId,
          -opposite.amount as amount,
          cl.ticker as currencyCode${conversionColumns('opposite', oppositeDateColumn)}
        from ${oppositeTableName} as opposite
        inner join accounts as acc
          on acc._id == opposite.account_id
        inner join currencies cur
          on cur._id == opposite.currency_id
        inner join available_currencies cl
          on cl._id == cur.available_currency_id
        inner join categories source_cat
          on source_cat._id == opposite.category_id
        left join categories target
          on target._id == source_cat.offset_category_id
          and target.is_deleted = 0
        left join categories target_parent
          on target_parent._id == target.parent_category_id
        where opposite.is_deleted == 0
          and coalesce(source_cat.reduces_opposite_type, 0) = 1
      )
      select
        ${columnsClause}
      from raw_data
      ${whereClause}
      ${groupByClause}
      ${orderByClause}
    `;

    const finalParams = config.currency !== 'all' ? [config.currency, ...params] : params;

    // console.debug('Executing SQL:', sql, 'with params:', finalParams);
    return this.query<{
      account?: string | undefined;
      category?: string | undefined;
      subcategory?: string | undefined;
      amount: number;
      currencyCode: string;
    }>(sql, finalParams);
  }

  private accountBalancesRowKey({ date_part, accountName }: { date_part: number; accountName: string }) {
    const key = `${date_part}|${accountName}`;
    return key;
  }

  private groupingDatesRowKey({ date_part }: { date_part: number }) {
    const key = `${date_part}`;
    return key;
  }

  private expensesIncomesReportRowKey(row: {
    account?: string | undefined;
    category?: string | undefined;
    subcategory?: string | undefined;
  }) {
    const key = `${row.account ?? ''}|${row.category ?? ''}|${row.subcategory ?? ''}`;
    return key;
  }

  private pivotData<
    TRow extends { amount: number; currencyCode: string },
    TItem extends { amounts: Record<string, number> },
  >(data: TRow[], createKey: (row: TRow) => string): TItem[] {
    const grouped = data.reduce(
      (acc, item) => {
        const key = createKey(item);
        let current = acc.map.get(key);
        if (!current) {
          const temp = { ...item, amounts: {} } as Partial<TRow>;
          delete temp.amount;
          delete temp.currencyCode;
          current = temp as unknown as TItem;
          acc.map.set(key, current);
          acc.items.push(current);
        }

        current.amounts[item.currencyCode] = (current.amounts[item.currencyCode] || 0) + item.amount;

        return acc;
      },
      {
        items: [],
        map: new Map<string, TItem>(),
      } as { items: TItem[]; map: Map<string, TItem> },
    );

    return grouped.items;
  }
}
